import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, Switch, Typography, Space, message,
  Row, Col, Select, InputNumber, Tabs, Alert, Tooltip, Badge, Tag, Modal, Spin
} from 'antd';
import {
  SaveOutlined, SettingOutlined, InfoCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ReloadOutlined, MonitorOutlined, LockOutlined,
  FileTextOutlined, ApiOutlined, DatabaseOutlined, SafetyOutlined,
  BellOutlined, SendOutlined, PlusOutlined, DeleteOutlined, SearchOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { handleApiError } from '../../utils/errorHandler';
import logger, { LogLevel, LogLevelNames } from '../../services/logger';
import RolesPermissions from './RolesPermissions';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Veri Yönetimi Tab Bileşeni — TimescaleDB sıkıştırma ve saklama ayarları
 */
const DataManagementTab = () => {
  const [policies, setPolicies] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dataForm] = Form.useForm();

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      setDataLoading(true);
      const response = await axios.get('/api/v1/database/timescale-policies');
      setPolicies(response.data);

      // Form değerlerini set et
      const values = {};
      response.data.retention?.forEach(r => {
        const days = parseInt(r.drop_after);
        values[`${r.table}_retention`] = isNaN(days) ? 365 : days;
      });
      response.data.compression?.forEach(c => {
        const days = parseInt(c.compress_after);
        values[`${c.table}_compression`] = isNaN(days) ? 30 : days;
      });
      dataForm.setFieldsValue(values);
    } catch (error) {
      message.error('TimescaleDB ayarları yüklenemedi');
    } finally {
      setDataLoading(false);
    }
  };

  const handleSave = async (values) => {
    Modal.confirm({
      title: 'Veri Yönetimi Ayarlarını Güncelle',
      content: (
        <div>
          <Alert
            type="warning"
            showIcon
            message="Saklama süresini kısaltmak eski verileri kalıcı olarak siler!"
            style={{ marginBottom: 8 }}
          />
          <p>Bu değişiklikler bir sonraki kontrol döngüsünde uygulanacaktır.</p>
        </div>
      ),
      okText: 'Onayla ve Kaydet',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          setSaving(true);
          const tables = ['telemetry_data_ts', 'device_logs_ts', 'application_logs_ts'];
          const body = {};
          tables.forEach(t => {
            body[t] = {
              retention_days: values[`${t}_retention`],
              compression_days: values[`${t}_compression`],
            };
          });
          await axios.put('/api/v1/database/timescale-policies', body);
          message.success('Veri yönetimi ayarları güncellendi');
          fetchPolicies();
        } catch (error) {
          message.error(error.response?.data?.detail || 'Güncelleme hatası');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const TABLE_LABELS = {
    telemetry_data_ts: { name: 'Telemetri Verileri', desc: 'Sensör verileri (sıcaklık, akış, sinyal)' },
    device_logs_ts: { name: 'Cihaz Logları', desc: 'Seans, hata, kontrol, config logları' },
    application_logs_ts: { name: 'Uygulama Logları', desc: 'Backend audit, güvenlik, sistem logları' },
  };

  if (dataLoading) return <Spin tip="Yükleniyor..." />;

  return (
    <Form form={dataForm} layout="vertical" onFinish={handleSave}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="TimescaleDB veri saklama ve sıkıştırma ayarları"
          description="Sıkıştırma disk alanı kazandırır. Saklama süresi dolunca veriler otomatik silinir."
        />

        {['telemetry_data_ts', 'device_logs_ts', 'application_logs_ts'].map(table => {
          const label = TABLE_LABELS[table];
          const sizeInfo = policies?.sizes?.find(s => s.table === table);
          return (
            <Card key={table} size="small"
              title={<span>{label.name} {sizeInfo && <Tag style={{ marginLeft: 8 }}>{sizeInfo.size} — {sizeInfo.chunks} chunk</Tag>}</span>}
            >
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>{label.desc}</Text>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name={`${table}_compression`} label="Sıkıştırma başlangıcı (gün)"
                    tooltip="Bu günden eski veriler sıkıştırılır. Veri kaybolmaz.">
                    <InputNumber min={1} max={365} style={{ width: '100%' }} addonAfter="gün sonra" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name={`${table}_retention`} label="Saklama süresi (gün)"
                    tooltip="Bu günden eski veriler otomatik silinir. Dikkat: geri alınamaz!">
                    <InputNumber min={7} max={3650} style={{ width: '100%' }} addonAfter="gün" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          );
        })}

        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
            Veri Yönetimi Ayarlarını Kaydet
          </Button>
        </Form.Item>
      </Space>
    </Form>
  );
};

/**
 * Bildirimler Tab Bileşeni
 */
const NotificationsTab = () => {
  const [notifForm] = Form.useForm();
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);

  // Telegram sohbet keşfi modal state'i
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoveredChats, setDiscoveredChats] = useState([]);
  const [discoverWarning, setDiscoverWarning] = useState(null);
  const [addedInSession, setAddedInSession] = useState(new Set());

  useEffect(() => {
    fetchNotifSettings();
  }, []);

  const fetchNotifSettings = async () => {
    try {
      setNotifLoading(true);
      const response = await axios.get('/api/v1/notifications/settings');
      const data = response.data || {};
      const all = Array.isArray(data.recipients) ? data.recipients : [];
      // Type'a göre ayır — form içinde iki ayrı Form.List
      const recipientsTelegram = all.filter(r => r.type === 'telegram');
      const recipientsEmail = all.filter(r => r.type === 'email');
      notifForm.setFieldsValue({
        ...data,
        recipients_telegram: recipientsTelegram,
        recipients_email: recipientsEmail,
      });
    } catch (error) {
      logger.error('Bildirim ayarları yüklenemedi:', error);
    } finally {
      setNotifLoading(false);
    }
  };

  const saveNotifSettings = async (values) => {
    try {
      setNotifSaving(true);
      const {
        recipients_telegram = [],
        recipients_email = [],
        ...rest
      } = values;

      // İki listeyi birleştir, type alanını garantile
      const recipients = [
        ...recipients_telegram.map(r => ({
          type: 'telegram',
          target: (r?.target || '').trim(),
          name: (r?.name || '').trim(),
          enabled: r?.enabled !== false,
        })),
        ...recipients_email.map(r => ({
          type: 'email',
          target: (r?.target || '').trim(),
          name: (r?.name || '').trim(),
          enabled: r?.enabled !== false,
        })),
      ].filter(r => r.target); // boş target'ları at

      await axios.put('/api/v1/notifications/settings', {
        ...rest,
        recipients,
      });
      message.success('Bildirim ayarları kaydedildi');
      fetchNotifSettings();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Kayıt hatası');
    } finally {
      setNotifSaving(false);
    }
  };

  const testRecipient = async (type, target) => {
    const trimmed = (target || '').trim();
    if (!trimmed) {
      message.warning('Önce hedef değerini girin');
      return;
    }
    try {
      const response = await axios.post('/api/v1/notifications/test/recipient', {
        type,
        target: trimmed,
      });
      if (response.data.success) {
        message.success(response.data.message);
      } else {
        message.error(response.data.error || response.data.message);
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Test hatası');
    }
  };

  const testTelegram = async () => {
    try {
      setTelegramTesting(true);
      const response = await axios.post('/api/v1/notifications/test/telegram');
      if (response.data.success) {
        message.success(response.data.message);
      } else {
        message.error(response.data.error || response.data.message);
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Test hatası');
    } finally {
      setTelegramTesting(false);
    }
  };

  const testEmail = async () => {
    try {
      setEmailTesting(true);
      const response = await axios.post('/api/v1/notifications/test/email');
      if (response.data.success) {
        message.success(response.data.message);
      } else {
        message.error(response.data.error || response.data.message);
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Test hatası');
    } finally {
      setEmailTesting(false);
    }
  };

  const fetchDiscoverChats = async () => {
    try {
      setDiscoverLoading(true);
      const response = await axios.get('/api/v1/notifications/telegram/discover-chats');
      setDiscoveredChats(response.data?.chats || []);
      setDiscoverWarning(response.data?.warning || null);
    } catch (error) {
      setDiscoveredChats([]);
      setDiscoverWarning(null);
      message.error(error.response?.data?.detail || 'Sohbetler alınamadı');
    } finally {
      setDiscoverLoading(false);
    }
  };

  const openDiscover = () => {
    setAddedInSession(new Set());
    setDiscoverOpen(true);
    fetchDiscoverChats();
  };

  const addDiscoveredChat = (chat) => {
    const currentList = notifForm.getFieldValue('recipients_telegram') || [];
    const exists = currentList.some((r) => (r?.target || '').trim() === chat.chat_id);
    if (exists) {
      message.info('Bu sohbet zaten alıcı listesinde');
      setAddedInSession((prev) => new Set(prev).add(chat.chat_id));
      return;
    }
    notifForm.setFieldsValue({
      recipients_telegram: [
        ...currentList,
        { name: chat.title, target: chat.chat_id, enabled: true },
      ],
    });
    setAddedInSession((prev) => new Set(prev).add(chat.chat_id));
    message.success(`"${chat.title}" eklendi — kaydetmeyi unutmayın`);
  };

  if (notifLoading) return <Spin tip="Yükleniyor..." />;

  return (
    <Form form={notifForm} layout="vertical" onFinish={saveNotifSettings}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Genel */}
        <Card title="Genel Ayarlar" size="small">
          <Form.Item name="enabled" label="Bildirim Sistemi" valuePropName="checked">
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="rate_limit_seconds" label="Rate Limit (saniye)"
                tooltip="Aynı bildirim bu süre içinde tekrar gönderilmez">
                <InputNumber min={30} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Telegram */}
        <Card title="Telegram" size="small"
          extra={
            <Space size="small">
              <Button icon={<SearchOutlined />} size="small" onClick={openDiscover}>
                Sohbet Keşfet
              </Button>
              <Button icon={<SendOutlined />} size="small" onClick={testTelegram} loading={telegramTesting}>
                Tümüne Test
              </Button>
            </Space>
          }>
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item name="telegram_enabled" label="Telegram Aktif" valuePropName="checked">
                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={18}>
              <Form.Item name="telegram_bot_token" label="Bot Token">
                <Input.Password placeholder="123456:ABC-DEF..." />
              </Form.Item>
            </Col>
          </Row>

          <Text strong style={{ display: 'block', marginBottom: 8 }}>Telegram Alıcıları</Text>
          <Form.List name="recipients_telegram">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} style={{ marginBottom: 8 }} align="top">
                    <Col xs={24} sm={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Etiket (ör. Ofis)" maxLength={100} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'target']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: 'Chat ID zorunlu' },
                          { pattern: /^-?\d+$/, message: 'Yalnızca rakam (grup için başta "-")' },
                        ]}
                      >
                        <Input placeholder="-1001234567890" maxLength={32} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={3}>
                      <Form.Item
                        {...restField}
                        name={[name, 'enabled']}
                        valuePropName="checked"
                        initialValue={true}
                        style={{ marginBottom: 0 }}
                      >
                        <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={5} style={{ textAlign: 'right' }}>
                      <Space size="small">
                        <Button
                          size="small"
                          icon={<SendOutlined />}
                          onClick={() => {
                            const row = notifForm.getFieldValue(['recipients_telegram', name]);
                            testRecipient('telegram', row?.target);
                          }}
                        >
                          Test
                        </Button>
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Space>
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ name: '', target: '', enabled: true })}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 4 }}
                >
                  Telegram Alıcısı Ekle
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        {/* Email */}
        <Card title="Email (SMTP)" size="small"
          extra={<Button icon={<SendOutlined />} size="small" onClick={testEmail} loading={emailTesting}>Tümüne Test</Button>}>
          <Row gutter={16}>
            <Col xs={24} sm={4}>
              <Form.Item name="email_enabled" label="Email Aktif" valuePropName="checked">
                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="smtp_host" label="SMTP Sunucu">
                <Input placeholder="smtp.gmail.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Form.Item name="smtp_port" label="Port">
                <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="587" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Form.Item name="smtp_use_tls" label="TLS" valuePropName="checked">
                <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Form.Item name="smtp_username" label="Kullanıcı">
                <Input placeholder="user@gmail.com" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="smtp_password" label="Şifre">
                <Input.Password placeholder="Uygulama şifresi" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="smtp_from_email" label="Gönderen Adres">
                <Input placeholder="flexer@firma.com" />
              </Form.Item>
            </Col>
          </Row>

          <Text strong style={{ display: 'block', marginBottom: 8 }}>Email Alıcıları</Text>
          <Form.List name="recipients_email">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} style={{ marginBottom: 8 }} align="top">
                    <Col xs={24} sm={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Etiket (ör. Servis)" maxLength={100} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'target']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: 'Email zorunlu' },
                          { type: 'email', message: 'Geçerli bir email giriniz' },
                        ]}
                      >
                        <Input placeholder="alici@firma.com" maxLength={255} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={3}>
                      <Form.Item
                        {...restField}
                        name={[name, 'enabled']}
                        valuePropName="checked"
                        initialValue={true}
                        style={{ marginBottom: 0 }}
                      >
                        <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={5} style={{ textAlign: 'right' }}>
                      <Space size="small">
                        <Button
                          size="small"
                          icon={<SendOutlined />}
                          onClick={() => {
                            const row = notifForm.getFieldValue(['recipients_email', name]);
                            testRecipient('email', row?.target);
                          }}
                        >
                          Test
                        </Button>
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Space>
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ name: '', target: '', enabled: true })}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 4 }}
                >
                  Email Alıcısı Ekle
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        {/* Kaydet */}
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={notifSaving}>
            Bildirim Ayarlarını Kaydet
          </Button>
        </Form.Item>
      </Space>

      {/* Telegram Sohbet Keşfi Modal */}
      <Modal
        open={discoverOpen}
        onCancel={() => setDiscoverOpen(false)}
        title="Telegram Sohbet Keşfi"
        width={680}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchDiscoverChats} loading={discoverLoading}>
            Yenile
          </Button>,
          <Button key="close" type="primary" onClick={() => setDiscoverOpen(false)}>
            Kapat
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message="Bot'un sizi görmesi için önce bir etkileşim olmalı"
          description={
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li><b>Kişisel DM:</b> Telegram'da botu bulup <code>/start</code> yazın</li>
              <li><b>Grup:</b> Botu gruba ekleyin ve herhangi bir mesaj gönderin</li>
              <li><b>Kanal:</b> Botu kanala admin olarak ekleyin ve bir post atın</li>
              <li>Ardından aşağıda <b>Yenile</b>'ye basın — son güncellemeler listelenecek</li>
            </ol>
          }
          style={{ marginBottom: 12 }}
        />
        {discoverWarning && (
          <Alert type="warning" showIcon message={discoverWarning} style={{ marginBottom: 12 }} />
        )}
        {discoverLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin tip="Telegram API'den okunuyor..." />
          </div>
        ) : discoveredChats.length === 0 ? (
          <Text type="secondary">Henüz bir sohbet bulunamadı.</Text>
        ) : (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {discoveredChats.map((chat) => {
              const isAdded = chat.already_added || addedInSession.has(chat.chat_id);
              return (
                <Row
                  key={chat.chat_id}
                  gutter={8}
                  align="middle"
                  style={{ padding: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}
                >
                  <Col flex="auto">
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                      <Space wrap size={4}>
                        <Text strong>{chat.title}</Text>
                        <Tag>{chat.type}</Tag>
                        {chat.username && (
                          <Text type="secondary">@{chat.username}</Text>
                        )}
                      </Space>
                      <Text code style={{ fontSize: 12 }}>{chat.chat_id}</Text>
                    </Space>
                  </Col>
                  <Col>
                    {isAdded ? (
                      <Tag color="success" icon={<CheckCircleOutlined />}>Eklendi</Tag>
                    ) : (
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => addDiscoveredChat(chat)}
                      >
                        Ekle
                      </Button>
                    )}
                  </Col>
                </Row>
              );
            })}
          </Space>
        )}
      </Modal>
    </Form>
  );
};

const Settings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState({});
  const [activeTab, setActiveTab] = useState('general');
  const [forceSyncLoading, setForceSyncLoading] = useState(false);
  const [frontendLogLevel, setFrontendLogLevel] = useState(logger.getLogLevel());
  const [backendLogLevel, setBackendLogLevel] = useState('INFO');

  useEffect(() => {
    fetchSettings();
    fetchSystemStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/system-settings');
      form.setFieldsValue(response.data);

      if (response.data.frontend_log_level !== undefined) {
        const dbLogLevel = response.data.frontend_log_level;
        logger.setLogLevel(dbLogLevel);
        setFrontendLogLevel(dbLogLevel);
      }
      if (response.data.log_level) {
        setBackendLogLevel(response.data.log_level);
      }
    } catch (error) {
      logger.error('Ayarlar yüklenirken hata:', error);
      handleApiError(error, 'Ayarlar yüklenirken hata oluştu', message.error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await axios.get('/api/v1/system-settings/status');
      setSystemStatus(response.data);
    } catch (error) {
      logger.error('Sistem durumu alınırken hata:', error);
    }
  };

  const handleForceMqttSync = async () => {
    Modal.confirm({
      title: 'MQTT ACL Zorla Senkronize Et',
      content: 'Bu işlem tüm cihaz MQTT izinlerini yeniden oluşturacak. Devam etmek istiyor musunuz?',
      okText: 'Evet, Senkronize Et',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          setForceSyncLoading(true);
          const response = await axios.post('/api/v1/system-settings/mqtt/force-sync');
          message.success(`${response.data.stats.synced} cihaz başarıyla senkronize edildi!`);
          await fetchSystemStatus();
        } catch (error) {
          handleApiError(error, 'MQTT ACL senkronizasyonu başarısız', message.error);
        } finally {
          setForceSyncLoading(false);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const allValues = { ...values, frontend_log_level: frontendLogLevel };
      await axios.put('/api/v1/system-settings', allValues);
      logger.setLogLevel(frontendLogLevel);
      if (values.log_level) setBackendLogLevel(values.log_level);
      message.success('Ayarlar başarıyla kaydedildi');
      fetchSystemStatus();
    } catch (error) {
      handleApiError(error, 'Ayarlar kaydedilirken hata oluştu', message.error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'running': case 'connected': case 'configured':
        return <Badge status="success" text="Çalışıyor" />;
      case 'not_configured':
        return <Badge status="warning" text="Yapılandırılmamış" />;
      default:
        return <Badge status="default" text="Bilinmiyor" />;
    }
  };

  if (loading && !systemStatus.system) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 48 }}>
        <Spin size="large" />
        <p>Ayarlar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <SettingOutlined /> Sistem Ayarları
        </Title>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ system_name: "Flexer Server", system_version: "1.0.0" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">

          {/* ===== TAB 1: GENEL ===== */}
          <Tabs.TabPane tab={<span><SettingOutlined /> Genel</span>} key="general">
            <Card>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="system_name" label="Sistem Adı" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="system_version" label="Sistem Versiyonu" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="admin_email" label="Admin E-posta" rules={[{ type: 'email', message: 'Geçerli e-posta girin' }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Tabs.TabPane>

          {/* ===== TAB 2: CİHAZ İZLEME ===== */}
          <Tabs.TabPane tab={<span><MonitorOutlined /> Cihaz İzleme</span>} key="device">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card title="Çevrimiçi/Çevrimdışı Kontrolü" size="small">
                <Alert
                  message="Cihaz Durum İzleme"
                  description="Cihazların online/offline durumunu belirlemek için kullanılan zaman aralıkları. Değişiklikler anında uygulanır."
                  type="info" showIcon style={{ marginBottom: 16 }}
                />
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="device_online_timeout_minutes" label={
                      <span>Online Timeout (dakika) <Tooltip title="Telemetri göndermeyen cihaz kaç dakika sonra offline olarak işaretlensin?"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    } rules={[{ required: true }]}>
                      <InputNumber min={1} max={60} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="device_offline_limit_days" label={
                      <span>Offline Limit (gün) <Tooltip title="Bu kadar gün offline kalan cihaz 'data pending' durumuna geçsin"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    } rules={[{ required: true }]}>
                      <InputNumber min={1} max={30} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="device_offline_check_interval_seconds" label={
                      <span>Kontrol Sıklığı (saniye) <Tooltip title="Offline kontrolü ne sıklıkla yapılsın?"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    } rules={[{ required: true }]}>
                      <InputNumber min={10} max={600} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Alert
                  message="Önerilen Değerler"
                  description={
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      <li><strong>Online Timeout:</strong> 5-15 dakika</li>
                      <li><strong>Offline Limit:</strong> 3-14 gün</li>
                      <li><strong>Kontrol Sıklığı:</strong> 30-120 saniye</li>
                    </ul>
                  }
                  type="success" showIcon
                />
              </Card>

              <Card title="OTA Güncelleme" size="small">
                <Alert
                  message="OTA Timeout: 30 dakika"
                  description="Cihaza OTA komutu gönderildikten sonra 30 dakika içinde yanıt alınamazsa güncelleme 'Zaman Aşımı' olarak işaretlenir. Bu değer şu an sabit olarak tanımlıdır."
                  type="info" showIcon
                />
              </Card>
            </Space>
          </Tabs.TabPane>

          {/* ===== TAB 3: GÜVENLİK ===== */}
          <Tabs.TabPane tab={<span><LockOutlined /> Güvenlik</span>} key="security">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card title="Oturum Ayarları" size="small">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="session_timeout_minutes" label={
                      <span>Oturum Süresi (dakika) <Tooltip title="Kullanıcı oturumunun maksimum süresi"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    }>
                      <InputNumber min={5} max={1440} style={{ width: '100%' }} placeholder="480" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="max_login_attempts" label={
                      <span>Maks. Giriş Denemesi <Tooltip title="Kaç başarısız denemeden sonra hesap kilitlensin"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    }>
                      <InputNumber min={3} max={20} style={{ width: '100%' }} placeholder="5" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card title="Şifre Politikası" size="small">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="password_min_length" label="Minimum Şifre Uzunluğu">
                      <InputNumber min={6} max={32} style={{ width: '100%' }} placeholder="8" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="password_require_uppercase" label="Büyük Harf Zorunlu" valuePropName="checked">
                      <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="password_require_numbers" label="Rakam Zorunlu" valuePropName="checked">
                      <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="password_require_special_chars" label="Özel Karakter Zorunlu" valuePropName="checked">
                      <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                    </Form.Item>
                  </Col>
                </Row>
                <Alert
                  message="Not: Küçük harf, ardışık karakter dizileri (123, abc) ve yaygın şifre kontrolü her zaman aktiftir."
                  type="info" showIcon
                />
              </Card>
            </Space>
          </Tabs.TabPane>

          {/* ===== TAB 4: LOGLAMA ===== */}
          <Tabs.TabPane tab={<span><FileTextOutlined /> Loglama</span>} key="logging">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card title="Backend Log Ayarları" size="small">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="log_level" label={
                      <span>Backend Log Seviyesi <Tooltip title="Sunucu tarafı loglarının detay seviyesi"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    }>
                      <Select>
                        <Option value="DEBUG">DEBUG - Tüm detaylar</Option>
                        <Option value="INFO">INFO - Genel bilgiler</Option>
                        <Option value="WARNING">WARNING - Uyarılar</Option>
                        <Option value="ERROR">ERROR - Sadece hatalar</Option>
                        <Option value="CRITICAL">CRITICAL - Kritik hatalar</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="log_retention_days" label={
                      <span>Log Saklama Süresi (gün) <Tooltip title="Bu süreden eski loglar otomatik temizlenir"><InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></Tooltip></span>
                    }>
                      <InputNumber min={1} max={365} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ padding: '0 0 8px' }}>
                      <Text type="secondary">Mevcut Seviye</Text>
                      <div><Tag color="green" style={{ fontSize: 14, padding: '4px 12px', marginTop: 4 }}>{backendLogLevel}</Tag></div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Restart gerekmez</Text>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card title="Frontend Log Ayarları" size="small">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Frontend Log Seviyesi">
                      <Select value={frontendLogLevel} onChange={setFrontendLogLevel}>
                        <Option value={LogLevel.DEBUG}>DEBUG - Tüm detaylar</Option>
                        <Option value={LogLevel.INFO}>INFO - Bilgi mesajları</Option>
                        <Option value={LogLevel.WARN}>WARN - Uyarılar</Option>
                        <Option value={LogLevel.ERROR}>ERROR - Hatalar</Option>
                        <Option value={LogLevel.NONE}>NONE - Kapalı</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ padding: '0 0 8px' }}>
                      <Text type="secondary">Mevcut Seviye</Text>
                      <div><Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', marginTop: 4 }}>{LogLevelNames[frontendLogLevel]}</Tag></div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Tarayıcı konsolu</Text>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card title="Audit Logging" size="small">
                <Form.Item name="enable_audit_logging" label="Denetim Kaydı" valuePropName="checked"
                  help="Kullanıcı işlemlerinin (giriş, CRUD, ayar değişikliği) loglanması">
                  <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                </Form.Item>
              </Card>
            </Space>
          </Tabs.TabPane>

          {/* ===== TAB 5: SİSTEM BİLGİSİ ===== */}
          <Tabs.TabPane tab={<span><DatabaseOutlined /> Sistem Bilgisi</span>} key="system-info">
            <Card>
              <Alert
                message="Bu bilgiler Docker Compose environment variables ile yönetilmektedir."
                type="info" showIcon style={{ marginBottom: 16 }}
              />

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Card size="small" title="Server">
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Host:</Text> <Tag color="blue">{systemStatus.websocket?.host || '0.0.0.0'}</Tag></div>
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Port:</Text> <Tag color="blue">{systemStatus.websocket?.port || '8000'}</Tag></div>
                    <div><Text type="secondary">SSL Port:</Text> <Tag color="blue">8443</Tag></div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small" title="Database">
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Status:</Text> <Tag color={systemStatus.database?.status === 'connected' ? 'green' : 'red'}>{systemStatus.database?.status || '?'}</Tag></div>
                    <div><Text type="secondary">Pool:</Text> <Tag color="green">{systemStatus.database?.connection_pool || 'Active'}</Tag></div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small" title="JWT">
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Algorithm:</Text> <Tag color="purple">HS256</Tag></div>
                    <div><Text type="secondary">Token Expire:</Text> <Tag color="purple">30 min</Tag></div>
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={8}>
                  <Card size="small" title="MQTT Backend">
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Status:</Text> <Tag color={systemStatus.mqtt_backend?.status === 'configured' ? 'green' : 'red'}>{systemStatus.mqtt_backend?.status || '?'}</Tag></div>
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Host:</Text> <Tag color="blue">{systemStatus.mqtt_backend?.host || 'mosquitto'}:{systemStatus.mqtt_backend?.port || '1883'}</Tag></div>
                    <div><Text type="secondary">TLS:</Text> <Tag color={systemStatus.mqtt_backend?.tls_enabled ? 'green' : 'orange'}>{systemStatus.mqtt_backend?.tls_enabled ? 'Aktif' : 'Pasif'}</Tag></div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small" title="MQTT Device">
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Status:</Text> <Tag color={systemStatus.mqtt_device?.status === 'configured' ? 'green' : 'red'}>{systemStatus.mqtt_device?.status || '?'}</Tag></div>
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Host:</Text> <Tag color="blue">{systemStatus.mqtt_device?.host || 'mosquitto'}:{systemStatus.mqtt_device?.port || '8883'}</Tag></div>
                    <div><Text type="secondary">TLS:</Text> <Tag color={systemStatus.mqtt_device?.tls_enabled ? 'green' : 'orange'}>{systemStatus.mqtt_device?.tls_enabled ? 'Aktif' : 'Pasif'}</Tag></div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small" title="SSL">
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Cert:</Text> <Tag color="orange">server.crt</Tag></div>
                    <div style={{ marginBottom: 8 }}><Text type="secondary">Key:</Text> <Tag color="orange">server.key</Tag></div>
                    <div><Text type="secondary">Port:</Text> <Tag color="orange">8443 (Flexer CA)</Tag></div>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Tabs.TabPane>

          {/* ===== TAB 6: VERİ YÖNETİMİ (Form dışında render edilir) ===== */}
          <Tabs.TabPane tab={<span><DatabaseOutlined /> Veri Yönetimi</span>} key="data">
            {/* İçerik Form dışında — aşağıda render */}
          </Tabs.TabPane>

          {/* ===== TAB 7: BİLDİRİMLER (Form dışında render edilir) ===== */}
          <Tabs.TabPane tab={<span><BellOutlined /> Bildirimler</span>} key="notifications">
            {/* İçerik Form dışında — aşağıda render */}
          </Tabs.TabPane>

          {/* ===== TAB 7: ROLLER ===== */}
          <Tabs.TabPane tab={<span><SafetyOutlined /> Roller ve Yetkiler</span>} key="roles">
            <RolesPermissions />
          </Tabs.TabPane>

          <Tabs.TabPane tab={<span><ApiOutlined /> MQTT</span>} key="mqtt">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card title="MQTT Durum Bilgisi" size="small">
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Card size="small" title="Backend Bağlantısı" bordered={false}>
                      {getStatusBadge(systemStatus.mqtt_backend?.status)}
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {systemStatus.mqtt_backend?.host || 'mosquitto'}:{systemStatus.mqtt_backend?.port || '1883'}
                          {' '}({systemStatus.mqtt_backend?.tls_enabled ? 'TLS' : 'Düz'})
                        </Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" title="Cihaz Bağlantısı" bordered={false}>
                      {getStatusBadge(systemStatus.mqtt_device?.status)}
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {systemStatus.mqtt_device?.host || 'mosquitto'}:{systemStatus.mqtt_device?.port || '8883'}
                          {' '}({systemStatus.mqtt_device?.tls_enabled ? 'TLS' : 'Düz'})
                        </Text>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Card>

              <Card title="MQTT ACL Yönetimi" size="small">
                <Alert
                  message="Dikkat: Tehlikeli İşlem"
                  description="Bu işlem tüm cihaz MQTT izinlerini silip yeniden oluşturur. Geçici olarak cihaz bağlantıları kopabilir."
                  type="warning" showIcon style={{ marginBottom: 16 }}
                />
                <Button
                  type="primary"
                  danger
                  onClick={handleForceMqttSync}
                  loading={forceSyncLoading}
                >
                  MQTT ACL Zorla Senkronize Et
                </Button>
              </Card>
            </Space>
          </Tabs.TabPane>

        </Tabs>

        {/* Save Button — Bildirimler ve Roller tab'larında gizle (kendi formları var) */}
        {activeTab !== 'notifications' && activeTab !== 'roles' && activeTab !== 'data' && (
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={fetchSettings} icon={<ReloadOutlined />}>Yenile</Button>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                Ayarları Kaydet
              </Button>
            </Space>
          </div>
        )}
      </Form>

      {/* Form dışında render edilen tab'lar */}
      {activeTab === 'data' && <DataManagementTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
    </div>
  );
};

export default Settings;
